import { useCallback, useRef, useState } from 'react'
import { ScanLine } from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import type { BarcodeMatch } from '@/types'
import { Button } from '@/components/ui/button'
import { useKeyboardWedgeScanner } from '@/hooks/useKeyboardWedgeScanner'
import { BarcodeScanDialog } from './BarcodeScanDialog'
import { IM } from './itemMasterStrings'
import { LinkBarcodeDialog } from './LinkBarcodeDialog'
import { getStoredUserRole } from '@/lib/staff-roles'
import { canLinkBarcodes } from '@/lib/inventoryFlow/barcodeLinkLogic'
import { BL } from '@/lib/inventoryFlow/barcodeLinkStrings'
import { friendlyError } from '@/lib/inventoryFlow/friendlyError'

export type ScanFeedback = { ok: boolean; text: string }

/**
 * One barcode scanner for a screen that works on a list (receive a PO, issue a
 * requisition, count an item): a USB gun anywhere on the screen, or the Scan
 * button's camera. Each code is looked up; `onItem` applies it and returns the
 * line to show ("Rice +25 kg" / "Rice is not on this order").
 *
 * Returns the Scan button + camera dialog + last-scan line to render, or null
 * when `enabled` is false (flag off → nothing new on screen).
 */
export function useItemScanner({ enabled, onItem }: {
  enabled: boolean
  onItem: (match: BarcodeMatch) => ScanFeedback
}) {
  const [cameraOpen, setCameraOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState<ScanFeedback | null>(null)
  // Stage 2D: an unknown code → "link this barcode to…", then carry on.
  const [unknownCode, setUnknownCode] = useState<string | null>(null)
  const onItemRef = useRef(onItem)
  onItemRef.current = onItem

  const resolve = useCallback(async (code: string) => {
    setBusy(true)
    try {
      // The kitchen flow's lookup: the /store one refuses the kitchen manager.
      const res = await apiClient.flowLookupBarcode(code)
      if (res.data) setFeedback(onItemRef.current(res.data))
      setCameraOpen(false)
    } catch (err) {
      setCameraOpen(false)
      const notFound = err instanceof ApiClientError && err.code === 'barcode_not_found'
      if (notFound && canLinkBarcodes(getStoredUserRole())) {
        setUnknownCode(code)
        setFeedback({ ok: false, text: IM.scan.notFound(code) })
        return
      }
      setFeedback({
        ok: false,
        text: notFound ? BL.unknownNoRights(code) : friendlyError(err),
      })
    } finally {
      setBusy(false)
    }
  }, [])

  useKeyboardWedgeScanner((code) => void resolve(code), { enabled: enabled && !cameraOpen && !unknownCode })

  const ui = enabled ? (
    <div className="flex flex-wrap items-center gap-3">
      <Button type="button" variant="outline" onClick={() => setCameraOpen(true)}>
        <ScanLine className="mr-2 h-4 w-4" /> {IM.scan.button}
      </Button>
      <span
        role="status"
        aria-live="polite"
        className={`text-sm font-medium ${feedback ? (feedback.ok ? 'text-success-fg' : 'text-danger-fg') : 'text-muted-foreground'}`}
      >
        {feedback?.text ?? IM.scan.listTip}
      </span>
      <BarcodeScanDialog open={cameraOpen} busy={busy} onClose={() => setCameraOpen(false)} onCode={(code) => void resolve(code)} />
      <LinkBarcodeDialog code={unknownCode} onClose={() => setUnknownCode(null)}
        onLinked={(m) => { setUnknownCode(null); setFeedback(onItemRef.current(m)) }} />
    </div>
  ) : null

  // lookup: a code typed by hand (e.g. into a search box) goes the same way as a scan.
  return { ui, feedback, lookup: resolve }
}
